/* eslint-disable max-lines */
/* eslint-disable indent */
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import ListItemComponent, {
  IStateListItem,
} from 'components/Abstract/ListItem';
import Alert from 'components/Shared/Alert';
import { IOption } from 'components/Shared/DropdownMenu';
import Snackbar from 'components/Shared/Snackbar';
import { dateFormat } from 'formatters/date';
import { NA } from 'interfaces/document';
import {
  getStatusLabel,
  IFile,
  statuses,
  statusFilters,
} from 'interfaces/file';
import { userRoles } from 'interfaces/user';
import { IUserToken } from 'interfaces/userToken';
import uniqBy from 'lodash/uniqBy';
import moment from 'moment';
import * as React from 'react';
import RxOp from 'rxjs-operators';
import fileService from 'services/file';

import StatusNoticeDialog from './StatusNoticeDialog';
import { getFileExpirationDate, getFileExpirationTime } from 'helpers/file';

interface IState extends IStateListItem {
  deleted?: boolean;
  openNoticeDialog: boolean;
  nextStatus?: statuses;
  options: IOption[];
}

interface IProps {
  file: IFile;
  documentFrequency: string;
  onEdit: (id: number) => void;
  onChangeStatus: (file: IFile) => void;
  onDeleteComplete: () => void;
  user: IUserToken;
}

export interface IChangeStatus {
  status: statuses;
  notice?: string;
  invalid?: boolean;
}

export default class ListItem extends ListItemComponent<IProps, IState> {
  readonly firstCellStyle: { wordBreak: 'break-word' };

  constructor(props: IProps) {
    super(props);
    this.state = {
      ...this.state,
      openNoticeDialog: false,
      options: [],
    };
  }

  componentDidMount = () => {
    this.setOptions();
  };

  setOptions = () => {
    const {
      user,
      file: { status },
    } = this.props;
    const options = user.roles.reduce((prev, role) => {
      const options = this.getOptionsForRole(
        role,
        user.id,
        this.props.file.creatorId,
        status
      );
      return [...prev, ...options];
    }, []);
    this.setState({ options: uniqBy(options, 'text') });
  };

  getOptionsForRole = (
    role: string,
    userId: number,
    creatorId: number,
    status: statuses
  ) => {
    switch (role) {
      case userRoles.admin.toString():
      case userRoles.sysAdmin.toString():
        return [
          {
            text: 'Editar',
            handler: this.handleEdit,
          },
          {
            text: 'Aprovar',
            handler: this.handleApprove,
          },
          {
            text: 'Aprovar com ressalva',
            handler: this.handleChangeApproveNotice,
          },
          {
            text: 'Reprovar',
            handler: this.handleDisapprove,
          },
          {
            text: 'Excluir',
            handler: this.handleDelete,
          },
        ];
      case userRoles.auditor.toString():
        if (status === statuses.PENDING) {
          if (status === statuses.PENDING) {
            return [
              {
                text: 'Editar',
                handler: this.handleEdit,
              },
              {
                text: 'Aprovar',
                handler: this.handleApprove,
              },
              {
                text: 'Aprovar com ressalva',
                handler: this.handleChangeApproveNotice,
              },
              {
                text: 'Reprovar',
                handler: this.handleDisapprove,
              },
            ];
          }
          return [];
        }
        return [];
      case userRoles.visitor.toString():
      case userRoles.venturer.toString():
      case userRoles.holding.toString():
      case userRoles.operator.toString():
        if (userId === creatorId && status === statuses.PENDING)
          return [
            {
              text: 'Excluir',
              handler: this.handleDelete,
            },
          ];
        return [];
      default:
        return [];
    }
  };

  cannotChangeStatusMessage = (): string => {
    return this.props.documentFrequency === NA ||
      this.props.file.frequency === NA
      ? 'Preencha a data de emissão'
      : 'Preencha as datas de emissão e de vencimento primeiramente';
  };

  canChangeStatus = (newStatus: statuses): boolean => {
    if (newStatus === statuses.DISAPPROV) {
      return true;
    }

    if (
      this.props.documentFrequency === NA ||
      this.props.file.frequency === NA
    ) {
      return !!this.props.file.emissionDate;
    }

    return !!this.props.file.emissionDate && !!this.props.file.expirationDate;
  };

  changeStatus = ({ status, notice, invalid }: IChangeStatus) => {
    if (this.canChangeStatus(status)) {
      const file = {
        ...this.props.file,
        status,
        notice,
        validationDate: new Date(),
        invalid,
      };
      this.setState({ loading: true });
      return fileService
        .updateStatus(file)
        .pipe(RxOp.bindComponent(this), RxOp.logError())
        .subscribe(
          (file) => {
            this.setState({ loading: false, openNoticeDialog: false });
            this.props.onChangeStatus(file);
          },
          () => {
            this.setState({ loading: false, openNoticeDialog: false });
            Snackbar.error(`Falha na aprovação do arquivo ${file.filename}`);
          }
        );
    }
    return Snackbar.error(this.cannotChangeStatusMessage());
  };

  handleChangeStatus = (status: statuses) => {
    if (this.canChangeStatus(status)) {
      return fileService
        .nextStatus(this.props.file.id, status)
        .pipe(RxOp.bindComponent(this), RxOp.logError())
        .subscribe(
          async ({ nextStatus }) =>
            this.setState({ openNoticeDialog: true, nextStatus }),
          () =>
            Snackbar.error(
              'Não é possível realizar essa ação para alterar o status atual.'
            )
        );
    }
    return Snackbar.error(this.cannotChangeStatusMessage());
  };

  handleChangeApproveNotice = () => {
    this.handleChangeStatus(statuses.APPROV_NOTICE);
  };

  handleApprove = () => {
    this.handleChangeStatus(statuses.APPROV);
  };

  handleDisapprove = () => {
    this.handleChangeStatus(statuses.DISAPPROV);
  };

  handleDelete = async () => {
    const { file, onDeleteComplete } = this.props;
    const title = `Deseja excluir o arquivo "${file.filename}"?`;
    const ok = await Alert.confirm({ title, mode: 'delete' });
    if (!ok) return;

    this.setState({ loading: true });
    onDeleteComplete();

    fileService
      .delete(file.id)
      .pipe(RxOp.logError(), RxOp.bindComponent(this))
      .subscribe(
        () => {
          Snackbar.show(`${file.filename} foi removido`);
          this.setState({ loading: false, deleted: true });
          onDeleteComplete();
        },
        (err) => {
          Snackbar.error(err.message);
          this.setState({ loading: false, deleted: false });
        }
      );
  };

  handleStatus = () => {
    const { file } = this.props;
    if (
      [statuses.APPROV, statuses.APPROV_NOTICE, statuses.DISAPPROV].includes(
        file.status
      ) &&
      file.notice
    ) {
      return (
        <Tooltip title={file.notice} interactive>
          <div>{getStatusLabel(file.status, undefined, file.invalid)}</div>
        </Tooltip>
      );
    }

    return getStatusLabel(file.status);
  };

  handleEdit = () => {
    const { file } = this.props;
    return this.props.onEdit(file.id);
  };

  render(): JSX.Element {
    const { deleted, openNoticeDialog, nextStatus, options } = this.state;
    const { file } = this.props;
    if (deleted) return null;

    return (
      <>
        <StatusNoticeDialog
          opened={openNoticeDialog}
          status={nextStatus}
          invalid={file.invalid}
          onSubmit={this.changeStatus}
          onCancel={() => this.setState({ openNoticeDialog: false })}
        />
        <TableRow>
          <TableCell
            scope="col"
            style={{
              maxWidth: 350,
              width: 350,
              overflow: 'hidden',
              overflowWrap: 'break-word',
            }}
          >
            <a target="_blank" rel="noopener noreferrer" href={file.url}>
              {file.filename}
            </a>
          </TableCell>
          <TableCell>
            {file.emissionDate ? dateFormat(new Date(file.emissionDate)) : '-'}
          </TableCell>
          <TableCell>{getFileExpirationDate(file)}</TableCell>
          <TableCell
            style={
              computeTime(file) && computeTime(file) <= 60
                ? { color: '#C01F3D', fontWeight: 'bold' }
                : { color: '#000000DE' }
            }
          >
            {getFileExpirationTime(file)}
          </TableCell>
          <TableCell>{file.frequency}</TableCell>
          <TableCell>{getSituation(file)}</TableCell>
          <TableCell>
            {file.uploadDate ? dateFormat(new Date(file.uploadDate)) : '-'}
          </TableCell>
          <TableCell>
            {file.validationDate
              ? dateFormat(new Date(file.validationDate))
              : '-'}
          </TableCell>
          <TableCell>{file.criticality}</TableCell>
          <TableCell>{this.renderSideMenu(options)}</TableCell>
        </TableRow>
      </>
    );
  }
}

export const getSituation = (file: IFile) => {
  const statusObj = statusFilters.find((filter) =>
    filter.statuses.some((s) => s.toString() === file.status)
  );

  if (statusObj) {
    return (
      <>
        {file.notice ? (
          <Tooltip
            style={{ fontSize: '12px' }}
            title={`${getStatusLabel(file.status, undefined, file.invalid)} - ${
              file.notice
            }`}
            interactive
          >
            <statusObj.icon />
          </Tooltip>
        ) : (
          <Tooltip
            title={getStatusLabel(file.status, undefined, file.invalid)}
            interactive
          >
            <statusObj.icon />
          </Tooltip>
        )}
      </>
    );
  } else {
    return '-';
  }
};

export const computeTime = (file: IFile) => {
  if (!file.expirationDate) return null;
  const dateDiff = moment(file.expirationDate).diff(moment());
  const duration = moment.duration(dateDiff);
  const asDays = duration.asDays();
  return asDays;
};
